// SMS Lead Bot — SMS shaping. PURE.
//
// The prompt tells the model it is writing SMS (short, no markdown, no walls of
// text). ⚠️ This is the belt to that braces: a model asked for plain text still
// occasionally returns **bold** or a bullet list, and those characters go out
// over SMS literally, on a client's behalf, looking like a broken system.
//
// ⚠️ IT NORMALISES, IT DOES NOT TRUNCATE. Cutting an SMS off mid-sentence to
// fit a segment budget produces a message that reads as an error and may change
// its meaning; the length is REPORTED to the approving human instead, who can
// edit it. Silently shortening a message a human is about to put their name to
// is the wrong kind of help.

/** Beyond this the approval queue warns; it never edits. Two GSM segments. */
export const SMS_SOFT_MAX_CHARS = 306;

/**
 * Strip the markdown a chat-trained model reaches for by habit. Deliberately
 * narrow — emphasis, headings, code ticks, bullet markers — because anything
 * cleverer risks mangling real text (a price like "1/2 price", an address).
 */
export function normalizeSmsDraft(text: string): string {
  if (typeof text !== 'string') return '';
  let out = text.replace(/\r\n?/g, '\n');
  // **bold** / __bold__ → bold. Paired only, so a lone asterisk survives.
  out = out.replace(/\*\*(.+?)\*\*/gs, '$1');
  out = out.replace(/__(.+?)__/gs, '$1');
  // Inline code / fences.
  out = out.replace(/```[a-z]*\n?/gi, '');
  out = out.replace(/`([^`]+)`/g, '$1');
  // Leading heading and blockquote markers.
  out = out.replace(/^\s{0,3}#{1,6}\s+/gm, '');
  out = out.replace(/^\s{0,3}>\s?/gm, '');
  // Bullet markers at the start of a line ("- ", "* ", "• ").
  out = out.replace(/^\s{0,3}[-*•]\s+/gm, '');
  // Collapse blank runs; an SMS has no paragraphs.
  out = out.replace(/\n{2,}/g, '\n');
  out = out.replace(/[ \t]{2,}/g, ' ');
  return out.trim();
}

// GSM 03.38 basic set. Anything outside it forces the whole message to UCS-2,
// which is why one stray emoji or curly quote costs more than half the length.
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXTENDED = '^{}\\[~]|€';

export interface SmsLength {
  characters: number;
  segments: number;
  encoding: 'GSM-7' | 'UCS-2';
}

/**
 * How this message will actually bill and split. Shown to the approving human
 * because a two-segment reply costs twice a one-segment reply, and because a
 * single emoji silently halving the limit is the kind of surprise worth
 * surfacing rather than explaining afterwards.
 */
export function measureSms(text: string): SmsLength {
  const value = typeof text === 'string' ? text : '';
  const chars = [...value];
  let gsm = true;
  let units = 0;
  for (const ch of chars) {
    if (GSM_BASIC.includes(ch)) {
      units += 1;
    } else if (GSM_EXTENDED.includes(ch)) {
      units += 2; // escape + character
    } else {
      gsm = false;
      break;
    }
  }
  if (!gsm) {
    // UCS-2 bills in 16-bit units, so an astral character (most emoji) is two.
    const ucs2Units = value.length;
    const single = 70;
    const multi = 67;
    return {
      characters: chars.length,
      segments: ucs2Units === 0 ? 0 : ucs2Units <= single ? 1 : Math.ceil(ucs2Units / multi),
      encoding: 'UCS-2',
    };
  }
  const single = 160;
  const multi = 153;
  return {
    characters: chars.length,
    segments: units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi),
    encoding: 'GSM-7',
  };
}
